import {
    AccountManager,
    api,
    AppInsights,
    Util,
    RequestResponse,
} from '@bluedolphin/util-module-mf';
import { useTenant } from '../tenant';
import { TrackingKeys } from '../../util/tracking-keys';
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useAzureMsal } from '../azure';
import { CDN_DT_URL, CDN_PROD_URL, LANDING_PAGE } from '../../util/constants';
import { UserRole } from '@bluedolphin/auth-mf/util/misc';

declare global {
    interface Window {
        CDN_ICON_ENDPOINT: string;
    }
}

type UserData = {
    avatar: string;
    default_landing_page: string;
    default_view_mode: boolean;
    email: string;
    external_id: string | boolean;
    first_name: string;
    id: string;
    isDeleted: boolean;
    isNew: boolean;
    is_guest_user: boolean;
    is_scim_provisioned: boolean;
    last_name: string;
    last_used_icons: Record<string, string>;
    last_used_shapes: Record<string, string>[];
    object_definition_permissions: Record<string, any>;
    profile: Record<string, any>;
    read_notifications: Record<string, any>[];
    roles: string[];
    user_name: string | null;
    visible_object_definitions: Record<string, any>[];
};

type UserContextValue = {
    user: UserData | null;
    updateUser: (
        userObj: any,
        id: any
    ) => Promise<
        | RequestResponse<any>
        | {
              errorMessage: string;
          }
    >;
    resetUser: () => void;
    userRoles: UserRole[];
};

export const UserContext = React.createContext<UserContextValue>(null);
/**
 * @param param0
 * @returns
 */
export function UserProvider({ children }): JSX.Element {
    const [user, setUser] = useState<UserData>(null);
    const [userRoles, setUserRoles] = useState<UserRole[]>(null);
    const { authenticationResult } = useAzureMsal();
    const { updateTenant } = useTenant();

    useEffect(() => {
        if (
            window.location.hostname &&
            (window.location.hostname.startsWith('tst') ||
                window.location.hostname.startsWith('dev') ||
                window.location.hostname.startsWith('cde') ||
                window.location.hostname.startsWith('ux'))
        ) {
            window.CDN_ICON_ENDPOINT = CDN_DT_URL;
        } else {
            window.CDN_ICON_ENDPOINT = CDN_PROD_URL;
        }
    }, []);

    useEffect(() => {
        if (authenticationResult?.accessToken) {
            (async function userManagement() {
                await signupUser();
                await validateUser();
                await getUserRoles();
            })();
        }

        window.addEventListener('REFRESH_AUTH_USER', validateUser);
        return () =>
            window.removeEventListener('REFRESH_AUTH_USER', validateUser);
    }, [authenticationResult]);

    /**
     * User signup validation
     */
    async function signupUser(): Promise<void> {
        const account = AccountManager.getTenant();
        const { inviteId, name, p_name } = account;

        if (inviteId) {
            try {
                await api.post({
                    url: '/user/signup',
                    body: {
                        invite_id: inviteId,
                        tenant: name,
                    },
                    headers: {
                        Tenant: name,
                        B2CAuthorization: p_name,
                    },
                });
                AppInsights.trackEvent(TrackingKeys.AuthSignupXhrSuccess, {});
            } catch (error) {
                AppInsights.trackEvent(TrackingKeys.AuthSignupXhrError, {
                    error,
                });
            }
            delete account.inviteId;
            AccountManager.setTenant(name, account);
        }
    }

    /**
     * User validation
     */
    async function validateUser(): Promise<void> {
        const account = AccountManager.getTenant();
        const { accessToken } = authenticationResult;
        const { deepLinkURL, name, p_name } = account;
        const redirect_url = deepLinkURL ? `/${deepLinkURL}` : '/';

        try {
            const userModel = await api.get<UserData>({
                url: '/user',
                headers: {
                    Tenant: name,
                    B2CAuthorization: p_name,
                    redirect_url,
                },
            });
            await updateTenant();
            const { data: user } = userModel;

            account.isGuestUser = user.is_guest_user || !user.roles.length;

            account.landingPage = LANDING_PAGE;

            if (user.default_landing_page.length) {
                account.landingPage = user.default_landing_page;
            }

            if (account.deepLinkURL) {
                account.landingPage = deepLinkURL;
            }

            delete account.deepLinkURL;

            delete account.id;
            delete account.b2cInstance;
            delete account.b2cPolicyName;

            delete account.logout;

            account.token = accessToken;
            account.exp = Util.JWT.getExpiration(accessToken);

            AccountManager.setTenant(account.name, account);
            AppInsights.addTelemetryInitializer(
                account.name,
                user?.profile?.details?.function[0] ?? 'no role'
            );
            AppInsights.trackEvent(TrackingKeys.AuthUserXhrSuccess, {});

            setUser(user);
        } catch (error) {
            setUser(null);
            AppInsights.trackEvent(TrackingKeys.AuthUserXhrError, {
                error,
            });
            Util.navigateWithError(account.rootURL, {
                name: 'user xhr',
                message: 'user_account_not_exists',
            });
        }
    }
    /**
     * Update User
     * @param updatedUser
     */
    async function updateUser(userObj, id) {
        const account = AccountManager.getTenant();

        try {
            const response = await api.post({
                url: `/user/updateuserprofile/${id}`,
                body: userObj,
            });
            AppInsights.trackEvent(TrackingKeys.AuthUserProfileUpdated, {
                userObj,
            });
            AppInsights.addTelemetryInitializer(
                account.name,
                userObj?.function ?? 'no role'
            );
            setUser(response.data);
            return response;
        } catch (error) {
            AppInsights.trackEvent(TrackingKeys.AuthUserProfileError, {
                error,
            });
            return { errorMessage: 'Error updating user profile' };
        }
    }

    /**
     * Get User Roles
     */
    async function getUserRoles() {
        try {
            const response = await window.fetch(
                window.CDN_ICON_ENDPOINT + 'assets/profile/user_roles.json'
            );
            const data = await response.json();
            data && setUserRoles(data);
        } catch (error) {
            AppInsights.trackEvent(TrackingKeys.AuthUserGetUserRolesError, {
                error,
            });
            return { errorMessage: 'Error getting user roles' };
        }
    }
    /**
     * resets the user to null
     */
    async function resetUser() {
        setUser(null);
    }

    const values = useMemo(
        () => ({ user, updateUser, resetUser, userRoles }),
        [user, updateUser, resetUser, userRoles]
    );

    return (
        <UserContext.Provider value={values}>{children}</UserContext.Provider>
    );
}

/**
 * A wrapper around the useContext Hook
 * Returns User context data
 *
 * @returns {UserContextValue}
 */
export function useUser(): UserContextValue | null {
    return useContext(UserContext);
}
